import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import { isStealthHidden } from "./StealthGuard.ts";
import { findHighestHpTargetWithLos } from "./Targeting.ts";
import { hasLineOfSight } from "../core/LineOfSight.ts";
import { COMBAT } from "../core/config.ts";
import { isTanky } from "./combatUtils.ts";
import type { TowerUpdateContext } from "./towerBehaviors.ts";
import {
  getDesignatedConfig,
  getRailArcSynergy,
  applyRailArc,
} from "./SynergyEffects.ts";

function resetToIdle(tower: Tower): void {
  tower.sniperState = "idle";
  tower.sniperStateTimer = 0;
  tower.sniperTarget = null;
  tower.sniperTargetVersion = 0;
  tower.sniperSnapShot = false;
}

function getTimers(tower: Tower): {
  aimTime: number;
  chargeTime: number;
  recoverTime: number;
} {
  return {
    aimTime: tower.special?.aimTime ?? 250,
    chargeTime: tower.special?.chargeTime ?? 1500,
    recoverTime: tower.special?.recoverTime ?? 400,
  };
}

function getDamage(
  tower: Tower,
  target: Enemy,
  ctx: TowerUpdateContext,
): number {
  let multiplier = 1;
  if (ctx.draftSystem) {
    multiplier *=
      ctx.draftSystem.getDamageMultiplier(tower.element) *
      ctx.draftSystem.getGlobalDamageMultiplier();
  }
  const elBonus = ctx.beam.elementBonuses[tower.element];
  if (elBonus !== undefined) multiplier *= elBonus;
  // APEX_PREDATOR: dominant element boosted, others penalized (null = neutral).
  if (ctx.beam.apex) {
    multiplier *=
      tower.element === ctx.beam.apex.dominant
        ? ctx.beam.apex.boost
        : ctx.beam.apex.penalty;
  }
  multiplier *= ctx.beam.globalDamageMult;
  // BL-175 / D-266 — signal's GOLD "Command Aura" buff (1 with no ORACLE in range
  // → byte-identical).
  multiplier *= tower.commandAuraMult;

  let dmg = Math.round(tower.damage * multiplier);
  if (target.marked) {
    dmg = Math.round(dmg * (1 + target.markDamageBonus));
  }
  if (isTanky(target)) {
    dmg = Math.round(dmg * ctx.beam.bossDamageMult);
  }
  return dmg;
}

function resolveShot(
  tower: Tower,
  target: Enemy,
  ctx: TowerUpdateContext,
  damageScale = 1,
): boolean {
  const baseDamage = getDamage(tower, target, ctx);
  const damage = Math.round(baseDamage * damageScale);
  const armorPierce = tower.level >= 3;

  // BL-175 / D-266 — kinetic's GOLD "ANNIHILATOR · Orbital Round": the charged
  // shot is an EXECUTE. A NON-boss in the lane is one-shot outright (deal its full
  // remaining HP+shield, ignoring resistance); a boss/tank instead eats
  // `goldExecuteBossFraction` of its MAX-hp on top of the normal hit. Gated on
  // `tower.isGold()` + the data so a sub-GOLD shot is byte-identical. The snap-shot
  // (recover-state follow-up) is NOT an execute — only a full charge annihilates.
  const goldExec =
    tower.isGold() &&
    damageScale === 1 &&
    tower.special?.goldExecuteBossFraction !== undefined;
  if (goldExec) {
    if (isTanky(target)) {
      const bossHit = Math.round(
        target.maxHp * tower.special!.goldExecuteBossFraction!,
      );
      const killed = target.takeDamage(damage + bossHit, tower.element, {
        ignoreResistance: true,
      });
      ctx.beam.bus.emit("projectile.hit", {
        projectile: { x: target.x, y: target.y, element: tower.element, color: tower.color },
        target,
        damage: damage + bossHit,
      });
      if (killed) target.active = false;
      return killed;
    }
    // Non-boss: guaranteed annihilation — deal whatever HP (+shield) remains.
    const lethal = Math.ceil(target.hp + target.shieldHp) + 1;
    target.takeDamage(lethal, tower.element, { ignoreResistance: true });
    ctx.beam.bus.emit("projectile.hit", {
      projectile: { x: target.x, y: target.y, element: tower.element, color: tower.color },
      target,
      damage,
    });
    target.active = false;
    return true;
  }

  const killed = target.takeDamage(damage, tower.element, {
    ignoreResistance: armorPierce,
  });

  // Hit feedback: the hitscan has no Projectile, so reuse projectile.hit with a
  // synthetic source so the existing handlers spawn floating damage + sparks.
  ctx.beam.bus.emit("projectile.hit", {
    projectile: {
      x: target.x,
      y: target.y,
      element: tower.element,
      color: tower.color,
    },
    target,
    damage,
  });

  if (killed) {
    target.active = false;
  }

  return killed;
}

function enemiesOnRay(
  tower: Tower,
  target: Enemy,
  enemies: Enemy[],
  rangeSq: number,
  halfWidth: number,
  losClear: (enemy: Enemy) => boolean,
): Enemy[] {
  const rayX = target.x - tower.x;
  const rayY = target.y - tower.y;
  const rayLen = Math.hypot(rayX, rayY);
  if (rayLen <= 0.0001) {
    return target.active && !isStealthHidden(target) && losClear(target)
      ? [target]
      : [];
  }

  const dirX = rayX / rayLen;
  const dirY = rayY / rayLen;
  const hits: Enemy[] = [];

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;

    const relX = enemy.x - tower.x;
    const relY = enemy.y - tower.y;
    const t = relX * dirX + relY * dirY;
    if (t < 0) continue;
    if (t * t > rangeSq) continue;

    const perpendicularDist = Math.abs(relX * dirY - relY * dirX);
    if (perpendicularDist > halfWidth) continue;
    if (!losClear(enemy)) continue;

    hits.push(enemy);
  }

  return hits;
}

/** Lifetime (ms) of the rail-tracer beam visual drawn on each shot. */
const RAIL_TRACER_MS = 160;
const SNIPER_BARREL_EASE_RATE = 12;

function updateBarrelAngle(tower: Tower, dt: number): void {
  const aim = tower.sniperTarget ?? tower.target;
  let desired = tower.barrelAngle;
  if (aim && aim.active) {
    desired = Math.atan2(aim.y - tower.y, aim.x - tower.x);
  } else if (tower.railShotTimer > 0) {
    desired = Math.atan2(tower.railShotY - tower.y, tower.railShotX - tower.x);
  }

  const delta = Math.atan2(
    Math.sin(desired - tower.barrelAngle),
    Math.cos(desired - tower.barrelAngle),
  );
  const step = Math.min(1, (dt / 1000) * SNIPER_BARREL_EASE_RATE);
  tower.barrelAngle += delta * step;
}

export function updateSniperTower(
  tower: Tower,
  dt: number,
  ctx: TowerUpdateContext,
): void {
  // Sniper drives its own cadence via the state machine, but still needs the
  // shared visual timers (recoil flash + rail tracer) decayed each frame —
  // the state machine never calls fire()/updateCooldown() otherwise.
  tower.updateCooldown(dt);
  updateBarrelAngle(tower, dt);

  const { aimTime, chargeTime, recoverTime } = getTimers(tower);
  const effectiveRange = tower.range * ctx.rangeMultiplier;
  const rangeSq = effectiveRange * effectiveRange;

  const desig = getDesignatedConfig(tower, ctx.activeSynergies);
  const arcSyn = getRailArcSynergy(tower, ctx.activeSynergies);

  const losClear = (enemy: Enemy): boolean =>
    ctx.losBlockedAt == null
      ? true
      : hasLineOfSight(tower.x, tower.y, enemy.x, enemy.y, ctx.losBlockedAt);

  const targetValid = (): boolean => {
    const target = tower.sniperTarget;
    if (!target) return false;
    if (!target.active) return false;
    if (target.version !== tower.sniperTargetVersion) return false;
    if (isStealthHidden(target)) return false;

    const dx = tower.x - target.x;
    const dy = tower.y - target.y;
    if (dx * dx + dy * dy > rangeSq) return false;
    if (!losClear(target)) return false;

    return true;
  };

  if (tower.sniperState === "idle") {
    const target = findHighestHpTargetWithLos(
      tower,
      ctx.enemies,
      effectiveRange,
      losClear,
      desig?.prioritizeMarked ?? false,
    );
    if (!target) {
      tower.target = null;
      return;
    }
    tower.sniperTarget = target;
    tower.sniperTargetVersion = target.version;
    tower.sniperSnapShot = false;
    tower.sniperState = "aiming";
    tower.sniperStateTimer = aimTime;
    tower.target = tower.sniperTarget;
    return;
  }

  if (tower.sniperState === "aiming") {
    if (!targetValid()) {
      resetToIdle(tower);
      tower.target = null;
      return;
    }
    tower.sniperStateTimer -= dt;
    if (tower.sniperStateTimer <= 0) {
      tower.sniperState = "charging";
      const aimTarget = tower.sniperTarget;
      tower.sniperStateTimer =
        desig && aimTarget?.marked
          ? chargeTime * desig.chargeMultiplierForMarked
          : chargeTime;
      tower.sniperChargeDuration = tower.sniperStateTimer;
      tower.sniperSnapShot = false;
    }
    tower.target = tower.sniperTarget;
    return;
  }

  if (tower.sniperState === "charging") {
    if (!targetValid()) {
      resetToIdle(tower);
      tower.target = null;
      return;
    }
    tower.sniperStateTimer -= dt;
    if (tower.sniperStateTimer <= 0) {
      const target = tower.sniperTarget;
      if (!target) {
        resetToIdle(tower);
        tower.target = null;
        return;
      }

      const wasSnapShot = tower.sniperSnapShot;
      const primaryX = target.x;
      const primaryY = target.y;
      const snapShotScale = wasSnapShot
        ? (tower.special?.snapShotMultiplier ?? 0.5)
        : 1;
      const primaryShotDamage = Math.round(
        getDamage(tower, target, ctx) * snapShotScale,
      );

      const hasPierce = tower.level >= 4;
      const firstVolley = hasPierce
        ? enemiesOnRay(
            tower,
            target,
            ctx.enemies,
            rangeSq,
            COMBAT.SNIPER_PIERCE_HALF_WIDTH,
            losClear,
          )
        : [target];

      for (const enemy of firstVolley) {
        resolveShot(tower, enemy, ctx, snapShotScale);
      }

      const primaryKilled = !target.active;

      if (!target.active) {
        tower.sniperTarget = null;
        tower.sniperTargetVersion = 0;
      }

      const hasDoubleShot = tower.level >= 5;
      if (hasDoubleShot) {
        const secondTarget = findHighestHpTargetWithLos(
          tower,
          ctx.enemies,
          effectiveRange,
          losClear,
          desig?.prioritizeMarked ?? false,
        );

        if (secondTarget) {
          const secondVolley = hasPierce
            ? enemiesOnRay(
                tower,
                secondTarget,
                ctx.enemies,
                rangeSq,
                COMBAT.SNIPER_PIERCE_HALF_WIDTH,
                losClear,
              )
            : [secondTarget];

          for (const enemy of secondVolley) {
            resolveShot(tower, enemy, ctx, snapShotScale);
          }
        }
      }

      tower.sniperSnapShot = false;
      tower.fireCooldownVisual = 80;
      // Rail tracer visual: beam from the tower to the shot endpoint, drawn by
      // TowerRenderer and faded out over RAIL_TRACER_MS.
      tower.railShotTimer = RAIL_TRACER_MS;
      tower.railShotX = primaryX;
      tower.railShotY = primaryY;
      ctx.beam.bus.emit("tower.fired", { tower, projectile: null });

      if (arcSyn && !wasSnapShot && primaryKilled) {
        applyRailArc(
          arcSyn,
          primaryX,
          primaryY,
          primaryShotDamage,
          ctx.enemies,
          ctx.beam.bus,
          new Set([target]),
        );
      }

      tower.sniperState = "recover";
      tower.sniperStateTimer = recoverTime;
    }
    tower.target = tower.sniperTarget;
    return;
  }

  if (tower.sniperState === "recover") {
    tower.sniperStateTimer -= dt;
    if (tower.sniperStateTimer <= 0) {
      if (targetValid()) {
        tower.sniperState = "charging";
        const snapTarget = tower.sniperTarget;
        tower.sniperStateTimer =
          desig && snapTarget?.marked
            ? chargeTime * desig.chargeMultiplierForMarked
            : chargeTime;
        tower.sniperChargeDuration = tower.sniperStateTimer;
        tower.sniperSnapShot = true;
      } else {
        tower.sniperState = "idle";
        tower.sniperStateTimer = 0;
        tower.sniperSnapShot = false;
      }
    }
    tower.target = tower.sniperTarget;
    return;
  }

  tower.target = tower.sniperTarget;
}
